import { httpsCallable } from "firebase/functions";
import { signInAnonymously } from "firebase/auth";
import { getKajikajyFirebaseClient } from "./firebaseClient";
import type {
  InvoiceStatus,
  KajikajyPlanId,
  PaymentIntegrationMode,
  PaymentProviderId
} from "@kajikajy/shared";

export interface CreateInvoicePayload {
  readonly businessName: string;
  readonly customerEmail?: string;
  readonly phoneNumber: string;
  readonly pin: string;
  readonly pinConfirmation: string;
  readonly planId: KajikajyPlanId;
}

export interface CreateInvoiceResult {
  readonly amountMGA: number;
  readonly currency: "MGA";
  readonly expiresAt: string;
  readonly invoiceId: string;
  readonly ok: boolean;
  readonly paymentReference: string;
  readonly status: InvoiceStatus;
}

export interface CreatePaymentSessionResult {
  readonly amountMGA: number;
  readonly checkoutUrl: string | null;
  readonly currency: "MGA";
  readonly expiresAt: string;
  readonly integrationMode: PaymentIntegrationMode;
  readonly invoiceId: string;
  readonly ok: boolean;
  readonly paymentReference: string;
  readonly provider: PaymentProviderId;
  readonly providerLabel: string;
  readonly status: InvoiceStatus;
}

export interface GetPaymentStatusPayload {
  readonly invoiceId: string;
  readonly paymentReference: string;
}

export interface GetPaymentStatusResult {
  readonly ok: boolean;
  readonly invoiceId: string | null;
  readonly paymentIntentId: string | null;
  readonly status: InvoiceStatus;
  readonly paidAt: string | null;
  readonly expiresAt: string | null;
}

function requireFirebaseClient() {
  const client = getKajikajyFirebaseClient();
  if (!client) {
    throw new Error("Firebase web config is missing.");
  }
  return client;
}

export async function ensureSubscriptionAuth() {
  const client = requireFirebaseClient();
  await client.auth.authStateReady();
  if (client.auth.currentUser) {
    return client.auth.currentUser;
  }
  const credential = await signInAnonymously(client.auth);
  return credential.user;
}

export async function createInvoice(payload: CreateInvoicePayload): Promise<CreateInvoiceResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<CreateInvoicePayload, CreateInvoiceResult>(
    client.functions,
    "createInvoice"
  );
  const result = await callable(payload);
  return result.data;
}

export async function createPaymentSession(
  invoiceId: string,
  provider?: PaymentProviderId
): Promise<CreatePaymentSessionResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<
    { readonly invoiceId: string; readonly provider?: PaymentProviderId },
    CreatePaymentSessionResult
  >(
    client.functions,
    "createPaymentSession"
  );
  const result = await callable({ invoiceId, provider });
  return result.data;
}

export async function getPaymentStatus(
  payload: GetPaymentStatusPayload
): Promise<GetPaymentStatusResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<GetPaymentStatusPayload, GetPaymentStatusResult>(
    client.functions,
    "getPaymentStatus"
  );
  const result = await callable(payload);
  return result.data;
}

export const createEfainaPayment = createPaymentSession;
